import { classNames } from '../utils/UNSAFE_classNames';
import { ComponentChildren, JSX } from 'preact';
import { Flex } from '../UNSAFE_Flex';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { sizeToCSS, Size } from '../utils/UNSAFE_size';
import { labelSlotStyles, labelInnerStyles, valueSlotStyles } from './LabelValueLayoutStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TextFieldRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/TextFieldTheme';
import { RadioRedwoodTheme } from '../UNSAFE_Radio/themes/redwood/RadioTheme';
import { LabelRedwoodTheme } from '../UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/FormLayoutTheme';

type Props = {
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines how the label is going to be positioned
   */
  labelEdge?: 'start' | 'top';
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;

  parentComponentVariant?: 'textField' | 'radioGroup';

  hasTopUserAssistance?: boolean;
};

export const LabelValueLayout = ({
  label,
  labelEdge,
  children,
  labelStartWidth = '33%',
  parentComponentVariant = 'textField',
  hasTopUserAssistance = false
}: Props) => {
  const { baseTheme: textFieldTheme } = useComponentTheme(TextFieldRedwoodTheme);
  const { baseTheme: radioTheme } = useComponentTheme(RadioRedwoodTheme);
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);

  const width = sizeToCSS(labelStartWidth);
  const valueWidth = `calc(100% - ${width})`;
  const labelStyles: JSX.CSSProperties =
    labelEdge === 'start' ? { flexBasis: width, width: width, maxWidth: width } : {};
  const valueStyles: JSX.CSSProperties =
    labelEdge === 'start' ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth } : {};

  const { isFormLayout, isReadonly: isFormReadonly, labelWrapping } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);
  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    labelEdge === 'start' && labelSlotStyles.start,
    // labelEdge === 'start' && isPureReadonly && labelSlotStyles.startReadonly,
    // labelEdge === 'start' && !isPureReadonly && labelSlotStyles.startNotPureReadonly,
    labelEdge === 'top' && labelSlotStyles.top,
    labelEdge === 'top' && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);
  const labelInnerClasses = classNames([
    parentComponentVariant === 'textField' && textFieldTheme,
    parentComponentVariant === 'radioGroup' && radioTheme,
    parentComponentVariant === 'radioGroup' && labelTheme,

    formLayoutTheme,
    labelInnerStyles.base,
    labelEdge === 'start' && labelInnerStyles.start,

    parentComponentVariant === 'textField' &&
      labelEdge === 'start' &&
      !isPureReadonly &&
      labelInnerStyles.startNotPureReadonly,

    parentComponentVariant === 'radioGroup' &&
      labelEdge === 'start' &&
      !hasTopUserAssistance &&
      labelInnerStyles.startWithNoTopRadioGroupUA,

    labelWrapping === 'truncate' && labelInnerStyles.noWrap
  ]);

  return (
    <Flex wrap="wrap" align="start">
      <div class={labelStyleClasses} style={labelStyles}>
        <div class={labelInnerClasses}>{label}</div>
      </div>
      <div class={valueSlotStyles} style={valueStyles}>
        {children}
      </div>
    </Flex>
  );
};
